import { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { fmtDuration } from "./jalali";

const pseudo = (seed: number, n = 40) => { let x = seed * 9301 + 49297; return Array.from({ length: n }, () => { x = (x * 9301 + 49297) % 233280; return 18 + Math.round((x / 233280) * 82); }); };

interface Props { src: string; duration?: number | null; waveform?: number[] | null; mine: boolean; seed: number }

export default function VoicePlayer({ src, duration, waveform, mine, seed }: Props) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [rate, setRate] = useState(1);
  const total = duration || 0;
  const bars = useMemo(() => (waveform && waveform.length > 4 ? waveform : pseudo(seed)), [waveform, seed]);

  useEffect(() => {
    const stopOthers = (e: Event) => { if ((e as CustomEvent).detail !== seed) { ref.current?.pause(); setPlaying(false); } };
    window.addEventListener("cx:voice-play", stopOthers);
    return () => { window.removeEventListener("cx:voice-play", stopOthers); ref.current?.pause(); };
  }, [seed]);

  const ensure = () => {
    if (ref.current) return ref.current;
    const a = new Audio(src); a.preload = "metadata";
    a.ontimeupdate = () => setT(a.currentTime);
    a.onended = () => { setPlaying(false); setT(0); };
    a.onpause = () => setPlaying(false); a.onplay = () => setPlaying(true);
    ref.current = a; return a;
  };
  const toggle = () => {
    const a = ensure();
    if (a.paused) { window.dispatchEvent(new CustomEvent("cx:voice-play", { detail: seed })); a.playbackRate = rate; a.play().catch(() => {}); } else a.pause();
  };
  const cycle = () => { const r = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1; setRate(r); if (ref.current) ref.current.playbackRate = r; };
  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const ratio = 1 - (e.clientX - box.left) / box.width;           // RTL: از راست شروع می‌شود
    const a = ensure(); const d = total || (isFinite(a.duration) ? a.duration : 0);
    if (d) { a.currentTime = Math.max(0, Math.min(d, ratio * d)); setT(a.currentTime); }
  };
  const progress = total ? Math.min(1, t / total) : 0;

  return (
    <div className={`cx-voice ${mine ? "mine" : ""}`}>
      <button className="cx-voice-play" onClick={toggle} aria-label={playing ? "توقف" : "پخش"}>{playing ? <Pause size={17} /> : <Play size={17} />}</button>
      <div className="cx-voice-wave" onClick={seek} role="slider" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
        {bars.map((h, i) => <i key={i} className={i / bars.length < progress ? "on" : ""} style={{ height: `${Math.max(14, h)}%` }} />)}
      </div>
      <div className="cx-voice-meta">
        <span>{fmtDuration(playing || t > 0 ? t : total)}</span>
        <button onClick={cycle} className="cx-voice-rate">{rate === 1 ? "۱×" : rate === 1.5 ? "۱٫۵×" : "۲×"}</button>
      </div>
    </div>
  );
}
